import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getSearchedGames,
  FetchGamesError,
} from '@/features/games/internal/helpers/browser-clients/get-searched-games';
import type { GameInfo } from '@/features/games/shared/types/game-info';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';
import { useCurrencyStore } from '@/features/shared/currency-store';
import { currencySelectors } from '@/features/currency-selector';

const DEFAULT_MIN_GAMES_SEARCH_TERM_LENGTH = 3;
const DEFAULT_DEBOUNCE_MS = 500;

export type GamesSearchError = 'searchError';
export type GamesSearchStatus = 'idle' | 'loading' | 'success' | 'error';

type UseGamesSearchOptions = {
  searchTerm: string;
  maxResults?: number;
  debounceMs?: number;
  minSearchTermLength?: number;
};

type UseGamesSearchResult = {
  status: GamesSearchStatus;
  isLoading: boolean;
  error: GamesSearchError | null;
  results: GameInfo[];
  displayedResults: GameInfo[];
  hasMoreResults: boolean;
  canSearch: boolean;
};

export const useGamesSearch = ({
  searchTerm,
  maxResults,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  minSearchTermLength = DEFAULT_MIN_GAMES_SEARCH_TERM_LENGTH,
}: UseGamesSearchOptions): UseGamesSearchResult => {
  const [results, setResults] = useState<GameInfo[]>([]);
  const [status, setStatus] = useState<GamesSearchStatus>('idle');
  const [error, setError] = useState<GamesSearchError | null>(null);
  const debouncedSearchTerm = useDebouncedValue(searchTerm, debounceMs);
  const abortControllerRef = useRef<AbortController | null>(null);

  const selectedCurrency = useCurrencyStore(currencySelectors.currency);

  const trimmedSearchTerm = debouncedSearchTerm.trim();
  const canSearch = trimmedSearchTerm.length >= minSearchTermLength;

  useEffect(() => {
    if (!canSearch) {
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setResults([]);
      setStatus('idle');
      setError(null);
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current?.abort();
    abortControllerRef.current = controller;

    const fetchLimit = typeof maxResults === 'number' ? maxResults + 1 : undefined;

    const fetchData = async () => {
      try {
        setStatus('loading');
        setError(null);

        const response = await getSearchedGames(trimmedSearchTerm, {
          limit: fetchLimit,
          signal: controller.signal,
        });

        if (controller.signal.aborted) {
          return;
        }

        setResults(response.games);
        setStatus('success');
      } catch (caughtError) {
        if (controller.signal.aborted) {
          return;
        }

        if (caughtError instanceof FetchGamesError) {
          setError('searchError');
        } else if (caughtError instanceof DOMException && caughtError.name === 'AbortError') {
          return;
        } else {
          setError('searchError');
        }

        setResults([]);
        setStatus('error');
      } finally {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    };

    void fetchData();

    return () => {
      controller.abort();
    };
  }, [canSearch, trimmedSearchTerm, maxResults, minSearchTermLength, selectedCurrency]);

  const displayedResults = useMemo(() => {
    if (typeof maxResults === 'number') {
      return results.slice(0, maxResults);
    }

    return results;
  }, [results, maxResults]);

  const hasMoreResults = useMemo(() => {
    if (typeof maxResults !== 'number') {
      return false;
    }

    return results.length > maxResults;
  }, [results, maxResults]);

  return {
    status,
    isLoading: status === 'loading',
    error,
    results,
    displayedResults,
    hasMoreResults,
    canSearch,
  };
};
